/**
 * **The board's hover preview, for any surface outside the command menu** (range-hover,
 * 2026-09-28: "any time I hover any weapon or ability in any menu at any depth, its range zone
 * should show up"). The combat app owns one `previewAim` (`useTargetingWashes`' hover input); the
 * command menu host sets it directly, and every other surface that lists a weapon or ability (the
 * unit sheet's loadout rows, the fixed-target Skirmish chooser's weapon cards) reaches it through
 * {@link useRangeHover}, which hands back the pointer and focus handlers to spread on the row.
 *
 * The rules the handlers carry, so a new surface gets them by spreading one object:
 * - a mouse or pen entering the row shows its aim's reach; leaving clears it;
 * - keyboard focus on the row or a control inside it shows it too (`:focus-visible`, so a click's
 *   focus adds nothing), blur clears it;
 * - touch shows nothing: a finger has no hover, and a tap already acts (no long-press);
 * - a row with no aim (a self-only system, a trait) shows nothing, and unmounting clears its preview.
 *
 * Each row clears only the preview it set, so a row leaving late never wipes the next row's.
 */
import { createContext, useContext, useEffect, useMemo, useRef, type FocusEvent, type PointerEvent } from "react";
import type { ActionDraft } from "./combatInspector";

export type RangePreview = {
  show: (owner: object, aim: ActionDraft) => void;
  clear: (owner: object) => void;
};

export const RangePreviewContext = createContext<RangePreview | null>(null);

/** The combat app's side: a {@link RangePreview} over its `setPreviewAim`, remembering who set it. */
export function useRangePreviewSource(setPreviewAim: (aim: ActionDraft | null) => void): RangePreview {
  const shownBy = useRef<object | null>(null);
  return useMemo(
    () => ({
      show: (owner, aim) => {
        shownBy.current = owner;
        setPreviewAim(aim);
      },
      clear: (owner) => {
        if (shownBy.current !== owner) return;
        shownBy.current = null;
        setPreviewAim(null);
      },
    }),
    [setPreviewAim],
  );
}

export type RangeHoverHandlers = {
  onPointerEnter?: (e: PointerEvent<HTMLElement>) => void;
  onPointerLeave?: () => void;
  onFocus?: (e: FocusEvent<HTMLElement>) => void;
  onBlur?: () => void;
};

/** The handlers that preview `aim`'s reach on the board while the row is hovered or keyboard-focused; `{}` with no aim or no board. */
export function useRangeHover(aim: ActionDraft | null | undefined): RangeHoverHandlers {
  const preview = useContext(RangePreviewContext);
  const owner = useRef({}).current;
  const aimRef = useRef(aim);
  aimRef.current = aim;
  useEffect(() => () => preview?.clear(owner), [preview, owner]);
  // A row whose aim goes away while shown (the unit left the board, the row changed) takes the preview with it.
  useEffect(() => {
    if (!aim) preview?.clear(owner);
  }, [aim, preview, owner]);
  if (!preview || !aim) return {};
  const show = () => {
    if (aimRef.current) preview.show(owner, aimRef.current);
  };
  const clear = () => preview.clear(owner);
  return {
    onPointerEnter: (e) => {
      if (e.pointerType !== "touch") show();
    },
    onPointerLeave: clear,
    onFocus: (e) => {
      // The focused element may be a control inside the row (React's focus events bubble).
      if (e.target.matches(":focus-visible")) show();
    },
    onBlur: clear,
  };
}
